import { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'components/UI/Modal/Modal';
import { removeSeats } from 'services/TicketService';
import { Button } from 'components/UI/Button/Button';
import { Link } from 'components/UI/Link/Link';
import { ReactComponent as SeatIcon } from 'assets/icons/svgs/icon-approved.svg';
import './Prompts.scss';

/**
 * Remove Seats Confirmation Modal
 * @param {*} props
 * @usage
 * <RemoveSeatsPrompt
 *  event={myevent}
 *  active={true}
 * />
 */
export const RemoveSeatsPrompt = (props) => {
  const { active, event, stepName, onRequestClose } = props;
  if (!event) {
    return null;
  }

  const [isBusy, setIsBusy] = useState(false);
  const [itemsRemoved, setItemsRemoved] = useState(false);
  const [error, setError] = useState(null);
  const [orderFromResponse, setOrderFromResponse] = useState(null);

  const noMoreItemsInCart = itemsRemoved && !error && orderFromResponse?.Cart.TotalItems === 0;
  const moreItemsInCart = itemsRemoved && !error && orderFromResponse?.Cart.TotalItems > 0;

  const closeModalHandler = () => {
    onRequestClose();
  };

  // Get line item id for seats
  const seatsLineItemId = event.LineItemId;

  /**
   * Handle Delete seats requests confirmed by user
   */
  const handleRemoveConfirmed = async () => {
    // Show loading state
    setIsBusy(true);
    try {
      // Delete seats on server and capture new order
      const response = await removeSeats(seatsLineItemId, event.PerformanceId);

      if (response.Status === 1) {
        setItemsRemoved(true);
        setOrderFromResponse(response.Order);
      } else {
        setError(true);
      }

      trackEvent('remove_from_cart', { seatsLineItemId, event });
      setIsBusy(false);
    } catch (e) {
      console.error(e);
      setIsBusy(false);
      setError(e);
    }
  };

  // Calculate how many seats/tickets are about to be removed in this event
  const numberOfTickets = event.SubLineItems.length;

  // Close the modal and reload the page
  // used to refresh the cart, summary, or payment data
  const closeAndReload = () => {
    onRequestClose();
    window.location.reload();
  };

  return (
    <Modal
      open={active}
      closeModalHandler={!orderFromResponse && closeModalHandler}
      extendClass="red-box padding-30 cart-remove-prompt"
    >
      <div className="text-center">
        <p>
          Just checking - do you really want to remove <br />
          <strong>
            {numberOfTickets} ticket{numberOfTickets > 1 ? 's' : ''}
          </strong>{' '}
          for <strong>{event.PerformanceDescription}</strong> from your cart?
        </p>

        {!itemsRemoved && (
          <Button
            onClick={handleRemoveConfirmed}
            disabled={isBusy}
            variant="primary"
            extendClass="btn-small"
            text={isBusy ? 'Removing...' : 'Yes, remove from my cart'}
          />
        )}

        {/* Success Banner */}
        {itemsRemoved && (
          <span className="btn action-complete">
            <SeatIcon className="prompt-icon" />
            Ticket{numberOfTickets > 1 ? 's' : ''} removed from cart
          </span>
        )}

        <section>
          {!itemsRemoved && !error && (
            <Link
              href="#"
              variant="primary"
              onClick={closeModalHandler}
              extendClass="modal-link link-font-bold"
            >
              Cancel
            </Link>
          )}

          {noMoreItemsInCart && (
            <div>
              <p>
                <Link
                  href={`${event.EventUrl}?findtickets=true`}
                  extendClass="modal-link link-font-bold"
                >
                  Find new seats for this event
                </Link>
              </p>
              <p>or</p>
              <p>
                <Link href="/whats-on" extendClass="modal-link link-font-bold  ">
                  Browse Other Events
                </Link>
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div>
              <p>Tickets cannot be removed at this time</p>
              <Link href="#" onClick={closeModalHandler} extendClass="modal-link">
                Cancel
              </Link>
            </div>
          )}
          {/* Return to 'stepName' link */}
          {moreItemsInCart && (
            <p>
              <Link href="#" onClick={closeAndReload} extendClass="modal-link">
                Return to {stepName}
              </Link>
            </p>
          )}
        </section>
      </div>
    </Modal>
  );
};

RemoveSeatsPrompt.propTypes = {
  active: PropTypes.bool.isRequired,
  event: PropTypes.object, // Event model from Sitecore
  stepName: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
};
